import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ConfirmModal from './shared/ConfirmModal';
import {
  FiCalendar, FiCheckCircle, FiRefreshCw, FiAlertCircle,
  FiCheck, FiX, FiClock, FiAlertTriangle, FiFileText,
  FiSearch, FiBarChart2, FiXCircle, FiTrendingDown, FiUsers,
  FiExternalLink, FiBell,
} from 'react-icons/fi';
import { fmtDate } from '../utils/istDate';

/* ─── Axios ─────────────────────────────────────────────────────── */
const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const initials = (name = '') =>
  name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '??';

/* ═══════════════════════════════════════════════════════════════
   SUMMARY MODAL
═══════════════════════════════════════════════════════════════ */
function SummaryModal({ emp, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!emp) return;
    setLoading(true);
    setData(null);
    api.get(`/leave/subordinate/${emp.employeeId}/monthly-balance/${year}`)
      .then(r => setData(r.data?.data ?? r.data))
      .catch(() => toast.error('Could not load summary'))
      .finally(() => setLoading(false));
  }, [emp, year]);

  if (!emp) return null;

  const modalNode = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/40"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: '#1d4ed8' }} className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center font-bold text-sm" style={{ color: '#1d4ed8' }}>
              {initials(emp.name)}
            </div>
            <div>
              <p className="font-bold text-white text-sm">{emp.name}</p>
              <p className="text-xs" style={{ color: '#bfdbfe' }}>{emp.employeeId} · Leave Summary {year}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <FiX size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4 bg-gray-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
              <FiRefreshCw className="animate-spin" size={20} />
              <p className="text-sm">Loading...</p>
            </div>
          ) : !data ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
              <FiAlertCircle size={20} />
              <p className="text-sm">No data available</p>
            </div>
          ) : (
            <>
              {/* Annual stats */}
              <div>
                <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">Annual</p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { label: 'Limit', value: data.annualLimit, sub: 'days/year' },
                    { label: 'Carry Fwd', value: data.totalCarryForward ?? data.carryForward, sub: 'prev year' },
                    { label: 'Earned Lv', value: data.earnedLeave ?? 0, sub: 'credited' },
                    { label: 'Paid Taken', value: data.paidTakenYear, sub: 'taken' },
                    { label: 'Remaining', value: data.totalPaidLeavesAvailableYear ?? data.currentAvailableBalance, sub: 'left' },
                  ].map(({ label, value, sub }) => (
                    <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">{label}</p>
                      <p className="text-xl font-black text-gray-900">{value ?? '—'}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly stats */}
              <div>
                <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">This Month</p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Monthly Cap', value: data.monthlyPaidLeaveLimit ?? 3, sub: 'max paid' },
                    { label: 'Paid Used', value: data.paidTakenMonth, sub: 'this month' },
                    { label: 'Slots Left', value: data.totalPaidLeavesAvailableMonth ?? Math.max(0, (data.monthlyPaidLeaveLimit ?? 3) - (data.paidTakenMonth || 0)), sub: 'available' },
                    { label: 'All Leaves', value: data.totalTakenMonth, sub: 'all types' },
                  ].map(({ label, value, sub }) => (
                    <div key={label} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">{label}</p>
                      <p className="text-xl font-black text-gray-900">{value ?? '—'}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* LOP */}
              {((data.lopLeavesMonth || 0) > 0 || (data.lopLeavesYear || 0) > 0) && (
                <div className="rounded-xl p-4 text-white" style={{ background: '#1e3a8a' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: '#93c5fd' }}>
                    <FiTrendingDown size={12} /> Loss of Pay (LOP)
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-2xl font-black">{data.lopLeavesMonth ?? 0} <span className="text-xs font-normal opacity-70">days</span></p>
                      <p className="text-xs mt-0.5" style={{ color: '#93c5fd' }}>This Month</p>
                    </div>
                    <div style={{ borderLeft: '1px solid rgba(255,255,255,0.15)', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                      <p className="text-2xl font-black">{data.lopLeavesYear ?? '—'} <span className="text-xs font-normal opacity-70">days</span></p>
                      <p className="text-xs mt-0.5" style={{ color: '#93c5fd' }}>This Year (FY)</p>
                    </div>
                    <div>
                      <p className="text-2xl font-black">
                        {data.lopDeductionAmount != null ? `₹${Number(data.lopDeductionAmount).toLocaleString('en-IN')}` : '—'}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: '#93c5fd' }}>Est. Deduction</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Leave history */}
              {[
                { key: 'pendingRequests', label: 'Pending' },
                { key: 'approvedRequests', label: 'Approved' },
                { key: 'rejectedRequests', label: 'Rejected' },
              ].map(({ key, label }) => {
                const list = data[key] || [];
                if (!list.length) return null;
                return (
                  <div key={key}>
                    <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">{label} ({list.length})</p>
                    <div className="space-y-1.5">
                      {list.map(l => (
                        <div key={l.id} className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full border" style={{ background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }}>{l.leaveType}</span>
                            <span className="text-xs text-gray-500">{fmtDate(l.startDate)} → {fmtDate(l.endDate)}</span>
                          </div>
                          <span className="text-sm font-bold text-gray-800">{l.daysCount}d
                            <span className="text-xs font-normal text-gray-400 ml-1">{l.paidDays ?? 0}P/{l.unpaidDays ?? 0}U</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalNode, document.body) : modalNode;
}

/* ═══════════════════════════════════════════════════════════════
   LEAVE ROW  (clean table-like card)
═══════════════════════════════════════════════════════════════ */
function LeaveRow({ leave, onApprove, onReject, onReverseReject, onOpenDetail, onRemindManager }) {
  const name = leave.employeeName || leave.employeeUsername || `#${leave.employeeId}`;
  const isPending = leave.status === 'PENDING';

  const statusConfig = {
    PENDING:   { label: 'Pending',   bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6' },
    APPROVED:  { label: 'Approved',  bg: '#f0fdf4', color: '#15803d', dot: '#22c55e' },
    REJECTED:  { label: 'Rejected',  bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444' },
    CANCELLED: { label: 'Cancelled', bg: '#f9fafb', color: '#6b7280', dot: '#9ca3af' },
  };
  const sc = statusConfig[leave.status] || statusConfig.PENDING;

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-6 py-5 flex items-start gap-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
      {/* Avatar */}
      <div 
        onClick={() => onOpenDetail && onOpenDetail(leave)}
        className="w-10 h-10 mt-0.5 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 cursor-pointer hover:opacity-90 transition-opacity" 
        style={{ background: '#008cd6' }}
        title="Click to view detailed info"
      >
        {initials(name)}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        
        {/* Header row: Name + Tags + Applied Date on right */}
        <div className="flex items-center gap-3 flex-wrap mb-1">
          <span 
            onClick={() => onOpenDetail && onOpenDetail(leave)}
            className="font-bold text-gray-900 text-base cursor-pointer hover:text-sky-600 transition-colors"
            title="Click to view detailed info"
          >
            {name}
          </span>
          
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold" style={{ background: sc.bg, color: sc.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: sc.dot, animation: isPending ? 'pulse 2s infinite' : 'none' }} />
              {sc.label}
            </span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full border shadow-sm" style={{ background: '#eff6ff', color: '#008cd6', borderColor: '#bfdbfe' }}>
              {leave.leaveType}
            </span>
            {leave.isHalfDay && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full border shadow-sm bg-indigo-50 text-indigo-700 border-indigo-200">
                Half Day ({leave.halfDaySession === 'SECOND_HALF' ? 'Second Half' : 'First Half'})
              </span>
            )}
            {(leave.unpaidDays || 0) > 0 && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white shadow-sm" style={{ background: '#111827' }}>
                LOP
              </span>
            )}
          </div>

          {leave.appliedDate && (
            <span className="flex items-center gap-1.5 text-xs text-gray-400 ml-auto">
              <FiClock size={12} /> Applied {fmtDate(leave.appliedDate)}
            </span>
          )}
        </div>

        {/* Sub-header: ID + Job Info */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
          {leave.employeeId && (
            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">#{leave.employeeId}</span>
          )}
          {(leave.jobTitle || leave.department) && (
            <span className="font-medium">
              {leave.jobTitle} {leave.jobTitle && leave.department && <span className="text-gray-300 px-1">•</span>} {leave.department}
            </span>
          )}
        </div>

        {/* Dates and Paid Info */}
        <div className="flex items-center gap-5 mt-2 text-sm text-gray-500">
          <span className="flex items-center gap-1.5 text-gray-700 font-medium">
            <FiCalendar style={{ color: '#008cd6' }} size={14} />
            {fmtDate(leave.startDate)}{leave.startDate !== leave.endDate && ` – ${fmtDate(leave.endDate)}`}
            <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded-lg ml-1">
              {leave.daysCount != null ? leave.daysCount : (leave.isHalfDay ? 0.5 : 1)}d
            </span>
          </span>
          {leave.paidDays != null && (
            <span className="font-semibold text-gray-600 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-100">
              {leave.paidDays} Paid {(leave.unpaidDays || 0) > 0 && <span className="text-red-600"> / {leave.unpaidDays} LOP</span>}
            </span>
          )}
        </div>
        
        {/* Reason Box */}
        {(leave.reason || leave.remarks) && (
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm flex flex-col gap-1.5">
            {leave.reason && (
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-gray-900 font-bold mr-2">Reason:</strong>{leave.reason}
              </p>
            )}
            {leave.remarks && (
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-gray-900 font-bold mr-2">HR Remarks:</strong>{leave.remarks}
              </p>
            )}
          </div>
        )}
        
        {/* Actions Bar with Detailed Info Button */}
        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100 flex-wrap">
          <button
            onClick={() => onOpenDetail && onOpenDetail(leave)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold border border-slate-200 text-slate-700 bg-slate-50 hover:bg-white hover:border-sky-300 hover:text-sky-600 transition-all shadow-sm cursor-pointer"
          >
            <FiExternalLink size={13} style={{ color: '#008cd6' }} /> Detailed Info &rarr;
          </button>

          <div className="flex items-center gap-2 ml-auto">
            {isPending && (
              <>
                <button
                  onClick={() => onRemindManager && onRemindManager(leave)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold border transition-all hover:bg-amber-50 shadow-sm cursor-pointer"
                  style={{ color: '#92400e', borderColor: '#fcd34d', background: '#fffbeb' }}
                  title="Send a reminder email and notification to this employee's manager"
                >
                  <FiBell size={13} /> Remind Manager
                </button>
                <button
                  onClick={() => onReject(leave)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold border transition-all hover:bg-gray-50 shadow-sm cursor-pointer"
                  style={{ color: '#374151', borderColor: '#d1d5db' }}
                >
                  <FiX size={13} /> Reject
                </button>
              </>
            )}
            {leave.status === 'REJECTED' && onReverseReject && (
              <button
                onClick={() => onReverseReject(leave)}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold border transition-all hover:bg-orange-100 shadow-sm cursor-pointer"
                style={{ color: '#c2410c', borderColor: '#fed7aa', background: '#fff7ed' }}
              >
                <FiRefreshCw size={13} /> Reverse Rejection
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════ */
const HrLeaveApprovals = () => {
  const navigate = useNavigate();
  const [allLeaves, setAllLeaves]     = useState([]);
  const [employees, setEmployees]     = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [error, setError]             = useState('');
  const [activeTab, setActiveTab]     = useState('PENDING');
  const [searchTerm, setSearchTerm]   = useState('');
  const [empSearch, setEmpSearch]     = useState('');
  const [actionRemark, setActionRemark] = useState('');
  const [confirm, setConfirm]         = useState({ open: false, leave: null, status: null, loading: false });
  const [summaryEmp, setSummaryEmp]   = useState(null);

  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isSuperadmin = ['SUPERADMIN', 'TESTER'].includes(String(currentUser.role || '').toUpperCase());

  /* ── Fetch team ───────────────────────────────────────────── */
  const fetchEmployees = useCallback(async () => {
    setLoadingEmps(true);
    try {
      const res = await api.get('/admin/users');
      const all = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      const filtered = isSuperadmin
        ? all
        : all.filter(e => String(e.managerId) === String(currentUser.id));
      setEmployees(filtered.map(e => ({
        id: e.id,
        employeeId: e.employeeId,
        name: [e.firstName, e.lastName].filter(Boolean).join(' ') || e.username || e.employeeId,
        jobTitle: e.jobTitle || '',
        department: e.department || '',
        isActive: e.isActive !== false,
      })));
    } catch (err) {
      console.error('Failed to load employees', err);
    } finally {
      setLoadingEmps(false);
    }
  }, [isSuperadmin, currentUser.id]);

  /* ── Fetch leaves ─────────────────────────────────────────── */
  const fetchLeaves = useCallback(async () => {
    setLoadingLeaves(true);
    setError('');
    try {
      const [pendingRes, allRes] = await Promise.allSettled([
        api.get('/leave/pending'),
        api.get('/leave/all?size=300&sort=appliedDate,desc'),
      ]);
      let pending = [];
      if (pendingRes.status === 'fulfilled') {
        const raw = pendingRes.value.data;
        pending = Array.isArray(raw) ? raw : (raw?.data ?? []);
      }
      let rest = [];
      if (allRes.status === 'fulfilled') {
        const raw = allRes.value.data;
        rest = Array.isArray(raw?.data?.content) ? raw.data.content
          : Array.isArray(raw?.content) ? raw.content
          : Array.isArray(raw?.data) ? raw.data
          : Array.isArray(raw) ? raw : [];
        const ids = new Set(pending.map(l => l.id));
        rest = rest.filter(l => !ids.has(l.id));
      }
      setAllLeaves([...pending, ...rest]);
    } catch {
      setError('Failed to load leave data. Please refresh.');
    } finally {
      setLoadingLeaves(false);
    }
  }, []);

  useEffect(() => { fetchLeaves(); fetchEmployees(); }, [fetchLeaves, fetchEmployees]);

  /* ── Counts + filters ─────────────────────────────────────── */
  const counts = React.useMemo(() => {
    let leaves = allLeaves;
    if (!isSuperadmin && employees.length > 0) {
      const subordinateIds = new Set(employees.map(e => String(e.employeeId)));
      leaves = leaves.filter(l => subordinateIds.has(String(l.employeeId)));
    }
    return {
      PENDING:  leaves.filter(l => l.status === 'PENDING').length,
      APPROVED: leaves.filter(l => l.status === 'APPROVED').length,
      REJECTED: leaves.filter(l => l.status === 'REJECTED' || l.status === 'CANCELLED').length,
    };
  }, [allLeaves, isSuperadmin, employees]);

  const filteredLeaves = React.useMemo(() => {
    let list = allLeaves.filter(l => {
      if (activeTab === 'PENDING')  return l.status === 'PENDING';
      if (activeTab === 'APPROVED') return l.status === 'APPROVED';
      if (activeTab === 'REJECTED') return l.status === 'REJECTED' || l.status === 'CANCELLED';
      return true;
    });

    if (!isSuperadmin && employees.length > 0) {
      const subordinateIds = new Set(employees.map(e => String(e.employeeId)));
      list = list.filter(l => subordinateIds.has(String(l.employeeId)));
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(l =>
        (l.employeeName || '').toLowerCase().includes(q) ||
        (l.employeeUsername || '').toLowerCase().includes(q) ||
        String(l.employeeId || '').includes(q) ||
        (l.leaveType || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allLeaves, activeTab, searchTerm, isSuperadmin, employees]);

  const filteredEmps = React.useMemo(() => {
    if (!empSearch) return employees;
    const q = empSearch.toLowerCase();
    return employees.filter(e =>
      e.name.toLowerCase().includes(q) ||
      (e.employeeId || '').toLowerCase().includes(q) ||
      (e.department || '').toLowerCase().includes(q)
    );
  }, [employees, empSearch]);

  /* ── Actions ──────────────────────────────────────────────── */
  const openConfirm = (leave, status) => {
    setActionRemark('');
    setConfirm({ open: true, leave, status, loading: false });
  };

  const handleAction = async () => {
    const { leave, status } = confirm;
    if (!leave) return;
    setConfirm(p => ({ ...p, loading: true }));
    try {
      if (status === 'REVERSE') {
        await api.put(`/leave/${leave.id}/reverse-rejection`, {});
        toast.success('Rejection reversed successfully');
      } else {
        const remarks = actionRemark.trim() || (status === 'APPROVED' ? 'Approved by HR' : 'Rejected by HR');
        await api.put('/leave/approve', { leaveId: leave.id, status, remarks });
        toast.success(`Leave ${status.toLowerCase()} successfully`);
      }
      setConfirm({ open: false, leave: null, status: null, loading: false });
      fetchLeaves();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
      setConfirm(p => ({ ...p, loading: false }));
    }
  };

  const TABS = [
    { key: 'PENDING',  label: 'Pending',  count: counts.PENDING },
    { key: 'APPROVED', label: 'Approved', count: counts.APPROVED },
    { key: 'REJECTED', label: 'Rejected', count: counts.REJECTED },
  ];

  const remindManager = async (leave) => {
    try {
      await api.post(`/leave/${leave.id}/remind-manager`);
      toast.success('Manager reminded via email and ERP notification ✅');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send reminder. Please try again.');
    }
  };

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Confirm modal */}
      <ConfirmModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, leave: null, status: null, loading: false })}
        onConfirm={handleAction}
        loading={confirm.loading}
        title={confirm.status === 'APPROVED' ? 'Approve Leave' : confirm.status === 'REJECTED' ? 'Reject Leave' : 'Reverse Rejection'}
        message={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '14px', color: '#374151' }}>
              {confirm.status === 'APPROVED'
                ? `Approve leave for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername}?`
                : confirm.status === 'REJECTED'
                ? `Reject leave for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername}? Loss of Pay (LOP) will apply.`
                : `Are you sure you want to reverse the rejection for ${confirm.leave?.employeeName || confirm.leave?.employeeUsername}? This will set it back to PENDING and remove the LOP deduction.`}
            </p>
            {confirm.status !== 'REVERSE' && (
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                  Remarks (optional)
                </label>
                <input
                  type="text"
                  placeholder="Add a note..."
                  value={actionRemark}
                  onChange={e => setActionRemark(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                />
              </div>
            )}
          </div>
        }
        confirmText={confirm.status === 'APPROVED' ? 'Approve' : confirm.status === 'REJECTED' ? 'Reject' : 'Reverse'}
        variant={confirm.status === 'APPROVED' ? 'info' : confirm.status === 'REVERSE' ? 'warning' : 'danger'}
      />

      {/* Summary modal */}
      {summaryEmp && <SummaryModal emp={summaryEmp} onClose={() => setSummaryEmp(null)} />}



      {/* ═══ LEAVE APPROVALS ═══════════════════════════════════ */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">

        {/* Tabs + controls */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-xl border border-gray-200 bg-gray-50">
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                style={activeTab === t.key
                  ? { background: '#008cd6', color: '#ffffff' }
                  : { color: '#6b7280' }
                }
              >
                {t.label}
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={activeTab === t.key
                    ? { background: 'rgba(255,255,255,0.25)', color: '#ffffff' }
                    : { background: '#e5e7eb', color: '#6b7280' }
                  }
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search + refresh */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <FiSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, type..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400"
                style={{ width: '200px' }}
              />
            </div>
            <button
              onClick={fetchLeaves}
              disabled={loadingLeaves}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              style={{ color: '#374151' }}
            >
              <FiRefreshCw size={12} className={loadingLeaves ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Leave list */}
        <div className="p-4" style={{ minHeight: '300px' }}>
          {loadingLeaves ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
              <FiRefreshCw className="animate-spin" size={20} style={{ color: '#008cd6' }} />
              <p className="text-sm">Loading leaves...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-500">
              <FiAlertCircle size={20} style={{ color: '#008cd6' }} />
              <p className="text-sm">{error}</p>
            </div>
          ) : filteredLeaves.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#eff6ff' }}>
                <FiCheckCircle size={20} style={{ color: '#008cd6' }} />
              </div>
              <p className="text-sm font-semibold text-gray-600">
                {activeTab === 'PENDING' ? 'All caught up — no pending requests' : `No ${activeTab.toLowerCase()} leaves`}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredLeaves.map(leave => (
                <LeaveRow
                  key={leave.id}
                  leave={leave}
                  onOpenDetail={lv => navigate(`/hr/leave/${lv.id}`, { state: { leave: lv } })}
                  onApprove={lv => openConfirm(lv, 'APPROVED')}
                  onReject={lv => openConfirm(lv, 'REJECTED')}
                  onReverseReject={lv => openConfirm(lv, 'REVERSE')}
                  onRemindManager={remindManager}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HrLeaveApprovals;
